import { InfoSet } from "../InfoSets/InfoSet";
import { MacAddress } from "../Core/MacAddress";
import { SnmpConstants } from "../Core/SnmpConstants";
import { BridgePortEntry, BridgePortTable } from "../InfoSets/BridgePortTable";
import { LinkMember } from "../Core/LinkMember";
import { Link } from "../Core/Link";
import { Dictionary } from "../Utils/Dictionary";
import { Device } from "../Core/Device";
import { HashSet } from "../Utils/HashSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { StringUtils } from "../Utils";

export class IfEntry implements ObjectSerializer {

	public static INVALID_IFINDEX = -1;

	// ifName/ifDescription CONSTANTS
	private static readonly bridgedVlanNameTag = "BV";
	private static readonly bridgedVlanDescrTag = "BVI";
	private static readonly ciscoManagementSc0Tag = "sc0";
	private static readonly ciscoManagementSl0Tag = "sl0";
	private static readonly ciscoManagementMe1Tag = "me1";
	private static readonly cpuDescrTag = "cpu ";
	private static readonly hostDataPortDescrTag = "host data port";
	private static readonly mngtDescrTag = "management port";
	private static readonly mngtInterfaceDescrTag = "management interface";
	private static readonly mngtInterfacePppTag = "management ppp interface";
	private static readonly nullDescrTag = "null";

	private static readonly vlanDescrTag = "vlan";
	private static readonly vlanRouterDescrTag = "vlan router";

	private static readonly dot11RadioDescrTag = "dot11radio";
	private static readonly dot11aTag = "dot11a";
	private static readonly dot11bTag = "dot11b";
	private static readonly virtualDot11RadioDescrTag = "virtual-dot11radio";
	private static readonly dot1qVlanSubIfDescrTag = "802.1q vlan subif";
	private static readonly wifiRadioDescrTag = "wifi";
	private static readonly wlanDescrTag = "wlan";

	private static readonly ethernetWFP = "ethernet-wfp";
	private static readonly ethernetNPCAP = "ethernet-npcap";
	private static readonly ethernetQOS = "ethernet-qos";
	private static readonly loopback = "loopback";

	public IfIndex: number;
	public get HasIfIndex(): boolean {
		return (this.IfIndex !== IfEntry.INVALID_IFINDEX);
	}

	public Alias: string;
	public get HasAlias(): boolean {
		return !!this.Alias;
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public Description: string;
	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public IfType: number;
	public get HasIfType(): boolean {
		return this.IfType > 0;
	}

	public Speed: number;
	public get HasSpeed(): boolean {
		return this.Speed > 0;
	}

	public AdminStatus: number;
	public get HasAdminStatus() {
		return this.AdminStatus >= 0;
	}

	public OperStatus: number;
	public get HasOperStatus(): boolean {
		return this.OperStatus != SnmpConstants.ifStatus_unknown;
	}

	public PoeAdminEnable: number;
	public get HasPoeAdminEnable(): boolean {
		return this.PoeAdminEnable != SnmpConstants.ifStatus_unknown;
	}

	public PoeDetectionStatus: number;
	public get HasPoeDetectionStatus(): boolean {
		return (this.PoeDetectionStatus > 0);
    }

	private _macAddress: MacAddress;
	public get MacAddress(): MacAddress {
		return this._macAddress;
	}

	public set MacAddress(value: MacAddress) {
		if (value)
			this._macAddress = value;
		else
			this._macAddress = MacAddress.CreateNullAddress();
	}

	public get HasMacAddress(): boolean {
		return this.MacAddress && !this._macAddress.IsNull;
	}

	// Dot1d BRIDGE PORT 
	public BridgePortIndex: number
	public get HasBridgePortIndex(): boolean {
		return this.BridgePortIndex > 0;
	}

	public get IsBridgePort(): boolean {
		return this.BridgePortIndex > 0;
	}

	// PHYSICAL interface (if the interface is a "virtual" interface layered on-top of a physical)
	public PhysicalIfIndex: number
	public get HasPhysicalIfIndex(): boolean {
		return this.PhysicalIfIndex >= 0;
	}

	// MAU
	public DuplexStatus: string;
	public get HasDuplexStatus(): boolean {
		return StringUtils.IsNullOrWhiteSpace(this.DuplexStatus);
	}

	public IsDuplexAutoNegotiateSupported: boolean;

	public get GetDuplexStatusDisplay(): string {
		let statusDisplay = "";

		if (this.HasDuplexStatus) {
			if (!this.IsOperStatusUp) {
				statusDisplay = "unknown";
				if (this.IsDuplexAutoNegotiateSupported === true) {
					statusDisplay = "auto";
				}
				else {
					if (!StringUtils.IsNullOrWhiteSpace(this.DuplexStatus))
						statusDisplay = this.DuplexStatus;
				}
			}
			else {
				if (this.DuplexStatus.toLowerCase() !== "unknown") {
					return this.DuplexStatus;
				}

				statusDisplay = "unknown";
				if (this.IsDuplexAutoNegotiateSupported === true) {
					statusDisplay = "auto";
				}
			}
		}

		return statusDisplay;
	}

	// CACHE
	public BridgePortEntry: BridgePortEntry;

	private _physIfEntry: IfEntry;
	public get PhysicalIfEntry(): IfEntry {
		if (this._physIfEntry)
			return this._physIfEntry;

		return this;
	}
	public set PhysicalIfEntry(value: IfEntry) {
		if (value) {
			this._physIfEntry = value;
			this.PhysicalIfIndex = value.IfIndex;
		}
		else {
			this._physIfEntry = null;
			this.PhysicalIfIndex = -1;
		}
	}

	public IsLagMember: boolean;

	public Radio: string;
	public get HasRadio(): boolean {
		return !StringUtils.IsNullOrWhiteSpace(this.Radio);
	}

	// Link / Topology
	public LinkMember: LinkMember;

	public get Link(): Link {
		if (this.LinkMember)
			return this.LinkMember.ParentLink;

		return null;
	}

	public RemoveLink() {
		this.LinkMember = null;
	}

	public ConnectedDevices: string;

	// TRAFFIC Counters
	public InOctets: number;
	public get HasInOctets(): boolean {
		return this.InOctets > 0;
	}

	public OutOctets: number;
	public get HasOutOctets(): boolean {
		return this.OutOctets > 0;
	}

	public InOctets_HC: number;
	public get HasInOctets_HC(): boolean {
		return this.InOctets_HC > 0;
	}

	public OutOctets_HC: number;
	public get HasOutOctets_HC(): boolean {
		return this.OutOctets_HC > 0;
	}

	public InOctets_ToString(): string {
		var octetStr = "0";

		if (this.HasInOctets_HC)
			octetStr = this.InOctets_HC.toString();
		else if (this.HasInOctets)
			octetStr = this.InOctets.toString();

		return octetStr;
	}

	public OutOctets_ToString(): string {
		var octetStr = "0";

		if (this.HasOutOctets_HC)
			octetStr = this.OutOctets_HC.toString();
		else if (this.HasOutOctets)
			octetStr = this.OutOctets.toString();

		return octetStr;
	}

	public get HasInOutOctetTraffic(): boolean {
		return (this.HasInOctets || this.HasInOctets_HC || this.HasOutOctets || this.HasOutOctets_HC);
	}

	public get DisplayName(): string {
		return this._DisplayName();
	}


	public get ShortDisplayName(): string {
		return this._DisplayName(true);
	}


	private _DisplayName(shortName: boolean = false): string {
		var displayName = "";

		if (this.HasAlias && this.HasName && !shortName)
			displayName = "[" + this.Alias + "] - " + this.Name;
		else if (this.HasAlias)
			displayName = this.Alias;
		else if (this.HasName)
			displayName = this.Name;
		else if (this.BridgePortEntry && this.BridgePortEntry.HasName)
			displayName = this.BridgePortEntry.Name;
		else {
			if (shortName)
				displayName = "If Index - " + this.IfIndex;
			else
				displayName = this.IfIndex + " - " + this.Description;
		}

		return (displayName);
	}

	public toString(): string {
		return this.DisplayName;
	}

	constructor(ifIndex?: number) {
		this._Init();
		this.IfIndex = ifIndex;
	}

	private _Init(): void {
		this.IfIndex = IfEntry.INVALID_IFINDEX;

		this.Name = '';
		this.Alias = '';
		this.Description = '';
		this.IfType = -1;
		this.Speed = 0;
		this.AdminStatus = -1;
		this.OperStatus = -1;
		this.PoeAdminEnable = SnmpConstants.pethPsePortAdminEnable_unknown;
		this.PoeDetectionStatus = -1;
		this.InOctets = 0;
		this.OutOctets = 0;
		this.InOctets_HC = 0;
		this.OutOctets_HC = 0;
		this._macAddress = MacAddress.CreateNullAddress();
		this.DuplexStatus = '';
		this.IsDuplexAutoNegotiateSupported = undefined;

		this.BridgePortIndex = -1;
		this.BridgePortEntry = null;

		this.PhysicalIfIndex = -1;
		this.PhysicalIfEntry = null;
		this.IsLagMember = false;
		this.Radio = '';

		this.LinkMember = null;
		this.ConnectedDevices = "";
	}

	private static ifSpeed_1_Kbps = 1000;
	private static ifSpeed_1_Mbps = 1000000;
	private static ifSpeed_1_Gbps = 1000000000;

	public static SpeedToString(speed: number): string {
		var ifSpeedText = '';

		if (speed >= IfEntry.ifSpeed_1_Gbps) {
			var newSpeed = speed / IfEntry.ifSpeed_1_Gbps;

			ifSpeedText = Number(newSpeed.toFixed(3)).toString() + " Gbps";
		}
		else if (speed >= IfEntry.ifSpeed_1_Mbps) {
			var newSpeed = speed / IfEntry.ifSpeed_1_Mbps;

			ifSpeedText = Number(newSpeed.toFixed(3)).toString() + " Mbps";
		}
		else if (speed >= IfEntry.ifSpeed_1_Kbps) {
			var newSpeed = speed / IfEntry.ifSpeed_1_Kbps;

			ifSpeedText = Number(newSpeed.toFixed(3)).toString() + " Kbps";
		}
		else {
			ifSpeedText = '' + speed;
		}

		return (ifSpeedText);
	}

	public SpeedToString(): string {
		return (IfEntry.SpeedToString(this.Speed));
	}

	public AdminStatusToString(): string {
		return (SnmpConstants.IfStatusToString(this.AdminStatus));
	}

	public OperStatusToString(): string {
		return (SnmpConstants.IfStatusToString(this.OperStatus));
	}

	public PoeAdminEnableToString(): string {
		return (SnmpConstants.PsePortAdminEnableToString(this.PoeAdminEnable));
	}

	public PoeDetectionStatusToString(): string {
		return (SnmpConstants.PsePortDetectionStatusToString(this.PoeDetectionStatus));
	}

	public get IsOperStatusUp(): boolean {
		var isStatusUp = false;

		switch (this.OperStatus) {
			case SnmpConstants.ifStatus_up:
			case SnmpConstants.ifStatus_testing:
			case SnmpConstants.ifStatus_unknown:
			case SnmpConstants.ifStatus_dormant:
				isStatusUp = true;
				break;
		}

		return isStatusUp;
	}

	public get IsOperStatusNotPresent(): boolean {
		var isStatusNotPresent = false;

		switch (this.OperStatus) {
			case SnmpConstants.ifStatus_notPresent:
				isStatusNotPresent = true;
				break;
		}

		return isStatusNotPresent;
	}

	public get IsLagInterface(): boolean {
		if (this.IfType == SnmpConstants.ifType_ieee8023adLag)
			return true;

		return this.PhysicalIfEntry && this.PhysicalIfEntry.IfType == SnmpConstants.ifType_ieee8023adLag;
	}

	public get IsLoopbackInterface(): boolean {
		return this.IfType == SnmpConstants.ifType_softwareLoopback;
	}

	public get IsLinkable(): boolean {
		if (this.IsLoopbackInterface)
			return false;

		if (this.IsVlanInterface)
			return false;

		// EXAMINE ifDescription/ifName filter non-linkable interfaces
		var lowerDescription = '';
		if (!!this.Description)
			lowerDescription = this.Description.trim().toLowerCase();

		// INTERFACES that represent CPU's; are not LINKABLE
		if (lowerDescription.indexOf(IfEntry.cpuDescrTag) == 0)
			return false;

		// INTERFACES that represent MANAGEMENT ports are not LINKABLE
		if (lowerDescription.indexOf(IfEntry.mngtDescrTag) >= 0)
			return false;

		// OTHER types of MANAGEMENT ports (not LINKABLE)
		if (this.IfType == SnmpConstants.ifType_other) {
			// HOST DATA PORT (i.e. Enterasys)
			if (lowerDescription.indexOf(IfEntry.hostDataPortDescrTag) >= 0)
				return false;

			// NULL identified interfaces
			if (lowerDescription.indexOf(IfEntry.nullDescrTag) == 0)
				return false;
		}

		// CHECK what the ifOperStatus of the interface is
		if (this.IsOperStatusUp) {
			if (this.Speed == 0) {
				// TODO: What does this MEAN???
			}

			if (lowerDescription.indexOf(IfEntry.mngtInterfaceDescrTag) >= 0)
				return false;

			if (lowerDescription.indexOf(IfEntry.mngtInterfacePppTag) >= 0)
				return false;

			// CISCO specfic
			if (lowerDescription.indexOf(IfEntry.ciscoManagementSc0Tag) == 0)
				return false;

			if (lowerDescription.indexOf(IfEntry.ciscoManagementSl0Tag) == 0)
				return false;

			if (lowerDescription.indexOf(IfEntry.ciscoManagementMe1Tag) == 0)
				return false;
		}
		else {
			if (this.IsOperStatusNotPresent)
				return false;

			if (this.Speed == 0) {
				// TODO: What does this mean???
			}
		}

		return true;
	}

	public get IsLinkable_Microsoft(): boolean {
		if (!this.HasMacAddress)
			return false;
		if (!this.IsOperStatusUp)
			return false;
		if (this.IsOperStatusNotPresent)
			return false;
		if (this.IsLoopbackInterface)
			return false;

		if (this.HasAlias) {
			let lowerAlias = this.Alias.toLowerCase();

			if (lowerAlias.indexOf(IfEntry.ethernetWFP) >= 0)
				return false;
			if (lowerAlias.indexOf(IfEntry.ethernetNPCAP) >= 0)
				return false;
			if (lowerAlias.indexOf(IfEntry.ethernetQOS) >= 0)
				return false;
			if (lowerAlias.indexOf(IfEntry.loopback) >= 0)
				return false;
		}

		return true;
	}

	public get IsLinked(): boolean {
		return this.LinkMember != null;
	}

	public get IsLogicalInterface(): boolean {
		let isLogicalType = false;

		if (this.IfType == SnmpConstants.ifType_propPointToPointSerial) {
			if (this.HasDescription) {
				let index = this.Description.toLowerCase().indexOf("logical-int");
				if (index >= 0)
					isLogicalType = true;
			}
		}

		return isLogicalType;
	}

	public get IsPhysicalInterface(): boolean {
		var isPhysicalType = true;

		switch (this.IfType) {
			case SnmpConstants.ifType_softwareLoopback:
			case SnmpConstants.ifType_slip:
			case SnmpConstants.ifType_rs232:
			case SnmpConstants.ifType_propVirtual:
			case SnmpConstants.ifType_tunnel:
			case SnmpConstants.ifType_l2vlan:
			case SnmpConstants.ifType_l3ipvlan:
			case SnmpConstants.ifType_l3ipxvlan:
				isPhysicalType = false;
				break;
		}

		return (isPhysicalType);
	}

	public get IsVlanInterface(): boolean {
		// CHECK WELL-KNOWN ifTypes
		if (this.IfType == SnmpConstants.ifType_propVirtual)
			return true;

		if (this.IfType == SnmpConstants.ifType_l2vlan)
			return true;

		if (this.IfType == SnmpConstants.ifType_l3ipvlan)
			return true;

		if (this.IfType == SnmpConstants.ifType_l3ipxvlan)
			return true;

		// EXAMINE the ifName / ifDescription  (for VLAN descriptions)
		var lowerName = '';
		var lowerDescription = '';

		if (!!this.Description)
			lowerDescription = this.Description.toLowerCase();

		// VLAN
		if (lowerDescription.indexOf(IfEntry.vlanDescrTag) >= 0)
			return true;

		// VLAN sub-interface
		if (lowerDescription.indexOf(IfEntry.dot1qVlanSubIfDescrTag) >= 0)
			return true;

		// VLAN/VIRTUAL radio interface
		if (lowerDescription.indexOf(IfEntry.virtualDot11RadioDescrTag) >= 0)
			return true;

		if (!!this.Name)
			lowerName = this.Name.toLowerCase();

		// BRIDGED VLANs (on wireless DEVICES)
		if (lowerName.indexOf(IfEntry.bridgedVlanNameTag) >= 0) {
			if (lowerDescription.indexOf(IfEntry.bridgedVlanDescrTag) >= 0)
				return true;
		}

		return false;
	}

	public get IsEthernet(): boolean {
		switch (this.IfType) {
			case SnmpConstants.ifType_ethernetCsmacd:
			case SnmpConstants.ifType_fastEther:
			case SnmpConstants.ifType_gigabitEthernet:
			case SnmpConstants.ifType_iso88023Csmacd:
			case SnmpConstants.ifType_starLan:
			case SnmpConstants.ifType_fastEtherFX:
				return true;
		}
		return false;
	}

	public get IsWireless(): boolean {
		return ((this.IfType == SnmpConstants.ifType_ieee80211) || this.IsDot11Radio);
	}

	public get IsDot11Radio(): boolean {
		if (this.Description) {
			var lowerDescription = this.Description.toLowerCase();
			if (lowerDescription.indexOf(IfEntry.dot11RadioDescrTag) >= 0) {
				return true;
			}

			if (this.IfType == SnmpConstants.ifType_ieee80211) {
				if ((lowerDescription.indexOf(IfEntry.dot11aTag) >= 0) || (lowerDescription.indexOf(IfEntry.dot11bTag) >= 0)) {
					return true;
				}
			}

			if (this.IfType == SnmpConstants.ifType_other) {
				if (StringUtils.StartsWith(lowerDescription, IfEntry.wifiRadioDescrTag)) {
					return true;
				}
			}

			if (StringUtils.StartsWith(lowerDescription, IfEntry.wlanDescrTag)) {
				return true;
			}
		}

		if (this.HasName) {
			let lowerName = this.Name.toLowerCase();
			if (lowerName.indexOf(IfEntry.wifiRadioDescrTag) >= 0) {
				if (this.IfType != SnmpConstants.ifType_l2vlan)
					return true;
			}
		}

		return false;
	}

	public get IsVirtualDot11Radio(): boolean {
		if (!!this.Description) {
			var lowerDescription = this.Description.toLowerCase();
			if (lowerDescription.indexOf(IfEntry.virtualDot11RadioDescrTag) >= 0)
				return true;
		}
		return false;
	}

	public get IsDot1qVlanSubInterface(): boolean {
		if (!!this.Description) {
			var lowerDescription: string = this.Description.toLowerCase();
			if (lowerDescription.indexOf(IfEntry.dot1qVlanSubIfDescrTag) >= 0)
				return true;
		}
		return false;
	}

	public get IsTransparentBridgeInterface(): boolean {
		// CHECK WELL-KNOWN ifTypes
		if (this.IfType == SnmpConstants.ifType_bridge) {
			return true;
		}

		return false;
	}

	public get IsTunnelInterface(): boolean {
		// CHECK WELL-KNOWN ifTypes
		if (this.IfType == SnmpConstants.ifType_tunnel)
			return true;

		return false;
	}

	public get IsPppMultilinkBundle(): boolean {
		if (this.IfType == SnmpConstants.ifType_pppMultilinkBundle)
			return true;

		return false;
	}

	public GetRadioIfDescription(): string {
		var ifDescr = '';

		if (!!this.Description)
			return ifDescr;

		var lowerDescription: string = this.Description.toLowerCase();

		var startIndex: number = lowerDescription.indexOf(IfEntry.dot11RadioDescrTag);
		if (startIndex >= 0) {
			var endIndex: number = lowerDescription.indexOf('.', startIndex);
			if (endIndex <= startIndex)
				endIndex = this.Description.length;

			ifDescr = this.Description.substring(startIndex, (endIndex - startIndex));
		}

		return (ifDescr);
	}

	public GetDot1qSubIfDescription(): string {
		var ifDescr = '';

		if (!!this.Description) {
			var endIndex: number = this.Description.indexOf(".");
			if (endIndex > 0)
				ifDescr = this.Description.substring(0, endIndex);
		}

		return (ifDescr);
	}

	public GetVirtualParentInterface(): string {
		var ifDescr = '';

		if (!!this.Description) {
			var endIndex: number = this.Description.lastIndexOf(".");
			if (endIndex > 0)
				ifDescr = this.Description.substring(0, endIndex);
		}

		return (ifDescr);
	}

	public static GetAdminStatus(ifData: string): number {
		if (!StringUtils.IsNullOrWhiteSpace(ifData)) {
			let index = -1;
			ifData = ifData.toLowerCase();

			index = ifData.indexOf("administratively down");
			if (index >= 0)
				return SnmpConstants.ifStatus_down;

			index = ifData.indexOf(" is up");
			if (index >= 0) {
				index = ifData.indexOf(" line protocol is up");
				if (index >= 0)
					return SnmpConstants.ifStatus_up;
			}

			index = ifData.indexOf(" is down");
			if (index >= 0) {
				index = ifData.indexOf("line protocol is down");
				if (index >= 0)
					return SnmpConstants.ifStatus_up;
			}
		}

		return SnmpConstants.ifStatus_unknown;
	}

	public static GetOperStatus(ifData: string): number {
		if (!StringUtils.IsNullOrWhiteSpace(ifData)) {
			let index = -1;
			ifData = ifData.toLowerCase();

			index = ifData.indexOf(" line protocol is up");
			if (index >= 0)
				return SnmpConstants.ifStatus_up;

			index = ifData.indexOf("line protocol is down");
			if (index >= 0)
				return SnmpConstants.ifStatus_down;
		}

		return SnmpConstants.ifStatus_unknown;
	}

	public static GetIfSpeed(ifData: string): number {
		let ifSpeed_1_Mbps = 1000000;
		let ifSpeed_10_Mbps = 10000000;
		let ifSpeed_100_Mbps = 100000000;
		let ifSpeed_1_Gbps = 1000000000;
		let ifSpeed_10_Gbps = 10000000000;
		let ifSpeed_100_Gbps = 100000000000;

		if (!StringUtils.IsNullOrWhiteSpace(ifData)) {
			let index = -1;
			ifData = ifData.toLowerCase();

			index = ifData.indexOf("1mb/s");
			if (index >= 0)
				return ifSpeed_1_Mbps;

			index = ifData.indexOf("10mb/s");
			if (index >= 0)
				return ifSpeed_10_Mbps;

			index = ifData.indexOf("100mb/s");
			if (index >= 0)
				return ifSpeed_100_Mbps;

			index = ifData.indexOf("1000mb/s");
			if (index >= 0)
				return ifSpeed_1_Gbps;

			index = ifData.indexOf("10000mb/s");
			if (index >= 0)
				return ifSpeed_10_Gbps;

			index = ifData.indexOf("100000mb/s");
			if (index >= 0)
				return ifSpeed_100_Gbps;

			index = ifData.indexOf("hardware is gigabit");
			if (index >= 0)
				return ifSpeed_1_Gbps;

			index = ifData.indexOf("hardware is ten gigabit");
			if (index >= 0)
				return ifSpeed_10_Gbps;


			index = ifData.indexOf("10/100base");
			if (index >= 0)
				return ifSpeed_100_Mbps;

			index = ifData.indexOf("10/100/1000base");
			if (index >= 0)
				return ifSpeed_1_Gbps;
		}

		return 0;
	}

	public static GetDuplex(ifData: string): string {
		if (!StringUtils.IsNullOrWhiteSpace(ifData)) {
			let index = -1;
			ifData = ifData.toLowerCase();

			index = ifData.indexOf("auto-duplex");
			if (index >= 0)
				return "auto";

			index = ifData.indexOf("full-duplex");
			if (index >= 0)
				return "full";

			index = ifData.indexOf("half-duplex");
			if (index >= 0)
				return "half";
		}

		return "";
	}

	public static GetIfType(ifData: string): number {
		if (!StringUtils.IsNullOrWhiteSpace(ifData)) {
			let index = -1;
			ifData = ifData.toLowerCase();

			index = ifData.indexOf("ethernet");
			if (index >= 0)
				return SnmpConstants.ifType_ethernetCsmacd;

			index = ifData.indexOf("unrouted vlan");
			if (index >= 0)
				return SnmpConstants.ifType_l2vlan;

			index = ifData.indexOf("vlan");
			if (index >= 0)
				return SnmpConstants.ifType_propVirtual;

			index = ifData.indexOf("vlan");
			if (index >= 0)
				return SnmpConstants.ifType_propVirtual;

			index = ifData.indexOf("stackport");
			if (index >= 0)
				return SnmpConstants.ifType_propVirtual;

			index = ifData.indexOf("stacksub");
			if (index >= 0)
				return SnmpConstants.ifType_propVirtual;
		}

		return SnmpConstants.ifType_other;
	}

	public Write(obj: any, context: any): void {
		obj.if_entry = {};
		obj = obj.if_entry;

		obj.if_index = ValueEncoder.EncodeInteger(this.IfIndex);

		if (this.HasAlias)
			obj.alias = this.Alias;

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasDescription)
			obj.description = this.Description;

		if (this.HasIfType)
			obj.type = ValueEncoder.EncodeInteger(this.IfType);

		if (this.HasSpeed)
			obj.speed = ValueEncoder.EncodeInteger(this.Speed);

		if (this.HasAdminStatus)
			obj.admin_status = ValueEncoder.EncodeInteger(this.AdminStatus);

		if (this.HasOperStatus)
			obj.oper_status = ValueEncoder.EncodeInteger(this.OperStatus);

		if (this.HasMacAddress)
			obj.mac_address = ValueEncoder.EncodeMacAddress(this.MacAddress);

		if (this.HasDuplexStatus)
			obj.duplex = this.DuplexStatus;

		if (this.IsDuplexAutoNegotiateSupported !== undefined)
			obj.duplex_auto_neg = ValueEncoder.EncodeBoolean(this.IsDuplexAutoNegotiateSupported);

		if (this.HasBridgePortIndex)
			obj.bridge_port_index = ValueEncoder.EncodeInteger(this.BridgePortIndex);

		if (this.HasPhysicalIfIndex)
			obj.physical_if_index = ValueEncoder.EncodeInteger(this.PhysicalIfIndex);

		if (this.HasRadio)
			obj.radio = this.Radio;

		// TRAFFIC Counters
		if (this.HasInOctets)
			obj.in_octets = ValueEncoder.EncodeInteger(this.InOctets);

		if (this.HasInOctets_HC)
			obj.in_octets_hc = ValueEncoder.EncodeInteger(this.InOctets_HC);

		if (this.HasOutOctets)
			obj.out_octets = ValueEncoder.EncodeInteger(this.OutOctets);

		if (this.HasOutOctets_HC)
			obj.out_octets_hc = ValueEncoder.EncodeInteger(this.OutOctets_HC);
	}

	public Read(obj: any, context: any): void {
		if (!obj.if_entry)
			throw new Error();
		obj = obj.if_entry;

		if (obj.if_index !== undefined)
			this.IfIndex = ValueEncoder.DecodeInteger(obj.if_index);

		if (obj.alias !== undefined)
			this.Alias = obj.alias;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.type !== undefined)
			this.IfType = ValueEncoder.DecodeInteger(obj.type);

		if (obj.speed !== undefined)
			this.Speed = ValueEncoder.DecodeInteger(obj.speed);

		if (obj.admin_status !== undefined)
			this.AdminStatus = ValueEncoder.DecodeInteger(obj.admin_status);

		if (obj.oper_status !== undefined)
			this.OperStatus = ValueEncoder.DecodeInteger(obj.oper_status);

		if (obj.mac_address !== undefined)
			this.MacAddress = ValueEncoder.DecodeMacAddress(obj.mac_address);

		if (obj.duplex !== undefined)
			this.DuplexStatus = obj.duplex;
		if (obj.suplex !== undefined)
			this.DuplexStatus = obj.suplex;

		if (obj.duplex_auto_neg !== undefined)
			this.IsDuplexAutoNegotiateSupported = ValueEncoder.DecodeBoolean(obj.duplex_auto_neg);

		if (obj.bridge_port_index !== undefined)
			this.BridgePortIndex = ValueEncoder.DecodeInteger(obj.bridge_port_index);

		if (obj.physical_if_index !== undefined)
			this.PhysicalIfIndex = ValueEncoder.DecodeInteger(obj.physical_if_index);

		if (obj.radio !== null)
			this.Radio = obj.radio;

		if (obj.in_octets !== undefined)
			this.InOctets = ValueEncoder.DecodeInteger(obj.in_octets);

		if (obj.in_octets_hc !== undefined)
			this.InOctets_HC = ValueEncoder.DecodeInteger(obj.in_octets_hc);

		if (obj.out_octets !== undefined)
			this.OutOctets = ValueEncoder.DecodeInteger(obj.out_octets);

		if (obj.out_octets_hc !== undefined)
			this.OutOctets_HC = ValueEncoder.DecodeInteger(obj.out_octets_hc);
	}

}

export class IfTable extends InfoSet {
	//public static FromJson(json: InfoSetEntriesJson<IfEntryJsonWrapper>, device: Device): IfTable {
	//	var result = new IfTable(device);

	//	json.entries.forEach((jsonEntry) => {
	//		var entry = IfEntry.FromJson(jsonEntry.if_entry);
	//		result._ifTable.Add(entry.IfIndex, entry);
	//	});

	//	return result;
	//}

	public ToArray(): IfEntry[] {
		return this._ifTable.Values().Values;
	}

	public ToLinkableArray(): IfEntry[] {
		var ifArray = [];
		if (ifArray !== null) {
			if (this.Device.IsMicrosoftWindows) {
				if (this._ifTable.Count > 0) {
					var tmpList = this._ifTable.Values().Values;

					for (var i = 0; i < tmpList.length; i++) {
						let ifEntry = tmpList[i];
						if (ifEntry) {
							if (ifEntry.IsLinkable_Microsoft == false)
								continue;
							ifArray.push(ifEntry);
						}
					}
				}
			}
			else {
				return this._ifTable.Values().Values;
			}
		}
		return ifArray;
	}

	private _ifTable: Dictionary<number, IfEntry>;

	constructor(device: Device) {
		super(device);
		this._ifTable = new Dictionary<number, IfEntry>();
	}

	public Add(ifIndex: number): IfEntry {
		var ifEntry = this._ifTable.Item(ifIndex);
		if (!ifEntry) {
			ifEntry = new IfEntry(ifIndex);
			this._ifTable.Add(ifIndex, ifEntry);
		}

		return ifEntry;
	}

	private AddEntry(ifEntry: IfEntry): void {
		this._ifTable.Add(ifEntry.IfIndex, ifEntry);
	}

	public Clear() {
		this._ifTable.Clear();
	}

	public get Count(): number {
		return this._ifTable.Count;
	}

	public Find(ifIndex: number): IfEntry {
		return this._ifTable.Item(ifIndex);
	}

	public FindByCdpDevicePort(cdpDevicePort: string): IfEntry {
		var foundIfEntry = null;

		foundIfEntry = this.FindByName(cdpDevicePort);
		if (!foundIfEntry)
			foundIfEntry = this.FindByDescription(cdpDevicePort);

		if (!foundIfEntry) {
			var ifIndex = parseInt(cdpDevicePort) || -1;

			if (ifIndex > 0) {
				foundIfEntry = this.Find(ifIndex);
			}
		}

		return (foundIfEntry);
	}

	public FindByName(ifName: string): IfEntry {
		var foundIfEntry: IfEntry = null;

		this._ifTable.Some((pair) => {
			if (pair.Value.HasName && pair.Value.Name.toLowerCase() == ifName.toLowerCase()) {
				foundIfEntry = pair.Value;
				return true;
			}
		});

		return (foundIfEntry);
	}

	public FindByDescription(ifDescription: string): IfEntry {
		var foundIfEntry: IfEntry = null;

		this._ifTable.Some((pair) => {
			if (pair.Value.Description && pair.Value.Description == ifDescription.toLowerCase()) {
				foundIfEntry = pair.Value;
				return true;
			}
		});

		return (foundIfEntry);
	}

	public FindByUniqueMacAddress(macAddress: MacAddress): IfEntry {
		var foundIfEntry: IfEntry = null;

		this._ifTable.Some((pair) => {
			if (pair.Value.HasMacAddress && pair.Value.MacAddress.Equals(macAddress)) {
				// Check if unique
				if (foundIfEntry) {
					foundIfEntry = null;
					return true;
				} else {
					foundIfEntry = pair.Value;
				}
			}
		});

		return (foundIfEntry);
	}

	public FindPhysicalEntry(ifIndex: number): IfEntry {
		var ifEntry: IfEntry = this.Find(ifIndex);
		if (!ifEntry)
			ifEntry = ifEntry.PhysicalIfEntry;

		return ifEntry;
	}

	public get HasMacAddresses(): boolean {
		return true;
	}

	public get MacAddresses(): HashSet<MacAddress> {
		var macAddresses = new HashSet<MacAddress>();

		this._ifTable.ForEach((pair) => {
			if (pair.Value.HasMacAddress)
				macAddresses.Add(pair.Value.MacAddress);
		});

		return (macAddresses);
	}

	public RemoveByIndex(ifIndex: number): boolean {
		return this._ifTable.Remove(ifIndex);
	}

	public Remove(ifEntry: IfEntry): boolean {
		return ifEntry ? this._ifTable.Remove(ifEntry.IfIndex) : false;
	}

	private ClearPhysicalIfEntryCache(): void {
		this._ifTable.ForEach((pair) => {
			pair.Value.PhysicalIfEntry = null;
		});
	}

	public BuildPhysicalIfEntryCache(): void {
		this.ClearPhysicalIfEntryCache();

		this._ifTable.ForEach((pair) => {
			if (pair.Value.HasPhysicalIfIndex)
				pair.Value.PhysicalIfEntry = this.Find(pair.Value.PhysicalIfIndex);
		});
	}

	public LookupPhysicalIfEntries(): void {
		this._ifTable.ForEach((pair) => {
			pair.Value.PhysicalIfIndex = -1;
			pair.Value.PhysicalIfEntry = null;
		});

		var physIfEntry: IfEntry = null;
		this._ifTable.ForEach((pair) => {
			var ifEntry: IfEntry = pair.Value;
			if (ifEntry.IsPhysicalInterface)
				return;

			physIfEntry = this.LookupPhysicalInterface(ifEntry);
			if (physIfEntry == null)
				return;

			if (physIfEntry == ifEntry)
				return;

			ifEntry.PhysicalIfIndex = physIfEntry.IfIndex;
			ifEntry.PhysicalIfEntry = physIfEntry;
		});

		this.LookupPhysicalIfEntiresForBridgePorts();
		this.LookupPhysicalIfEntiresForLagPorts();
	}

	public LookupPhysicalIfEntiresForBridgePorts(): void {
		// NOTE: This method is primarily used to "fix-up" interfaces of Juniper devices
		if (!this.Device.IsJuniperDevice)
			return;

		var physIfEntry: IfEntry = null;
		var lookupDescr = '';

		this._ifTable.ForEach((pair) => {
			var ifEntry: IfEntry = pair.Value;

			// CHECK to see if this INTERFACE is a VIRTUAL type
			if (ifEntry.IfType != SnmpConstants.ifType_propVirtual)
				return;

			if ((ifEntry.IfIndex == 713) || (ifEntry.IfIndex == 715)) {
				var iTest = 0;
			}

			lookupDescr = ifEntry.GetVirtualParentInterface();
			if (!lookupDescr)
				return;

			physIfEntry = this.FindByDescription(lookupDescr);
			if (physIfEntry == null)
				return;

			ifEntry.PhysicalIfIndex = physIfEntry.IfIndex;
			ifEntry.PhysicalIfEntry = physIfEntry;
		});
	}

	public LookupPhysicalIfEntiresForLagPorts(): void {
		// NOTE: This method is primarily used to "fix-up" interfaces of Juniper devices
		if (!this.Device.IsJuniperDevice)
			return;

		var physIfEntry: IfEntry = null;
		var lookupDescr = "";

		this._ifTable.ForEach((pair) => {
			var ifEntry: IfEntry = pair.Value;
			// CHECK to see if this INTERFACE is a LAG type
			if (ifEntry.IfType != SnmpConstants.ifType_ieee8023adLag)
				return;

			lookupDescr = ifEntry.GetVirtualParentInterface();
			if (!lookupDescr)
				return;

			physIfEntry = this.FindByDescription(lookupDescr);
			if (physIfEntry == null)
				return;

			ifEntry.PhysicalIfIndex = physIfEntry.IfIndex;
			ifEntry.PhysicalIfEntry = physIfEntry;
		});
	}

	private LookupPhysicalInterface(ifEntry: IfEntry): IfEntry {
		if (ifEntry == null)
			return null;

		// NOTE: If the interface is PHYSICAL; just return
		if (ifEntry.IsPhysicalInterface)
			return ifEntry;

		var physIfEntry: IfEntry = null;

		var physIfDescription = '';

		// EXAMINE the ifEntry to see it is of VIRTUAL type
		if (ifEntry.IsDot1qVlanSubInterface)
			physIfDescription = ifEntry.GetDot1qSubIfDescription();
		else if (ifEntry.IsDot11Radio)
			physIfDescription = ifEntry.GetRadioIfDescription();

		// LOOKUP the PHYSICAL ifEntry
		physIfEntry = this.FindByDescription(physIfDescription);
		if (physIfEntry != null) {
			// DOUBLE CHECK (VLANs aren't great PHYSICAL interfaces)
			if (physIfEntry.IsVlanInterface)
				physIfEntry = null;
		}

		// NOTE: At this point we DON'T have a good alternative
		if (physIfEntry == null)
			physIfEntry = ifEntry;

		//	Ensure a physical interface
		if (!physIfEntry.IsPhysicalInterface) {
			if (physIfEntry.HasMacAddress && !physIfEntry.MacAddress.IsNull) {
				this._ifTable.Some((pair) => {
					var tempIfEntry: IfEntry = pair.Value;
					if (!tempIfEntry.IsPhysicalInterface)
						return;

					if (!tempIfEntry.HasMacAddress)
						return;

					if (!tempIfEntry.MacAddress.Equals(physIfEntry.MacAddress))
						return;

					physIfEntry = tempIfEntry;
					return true;
				});
			}
		}

		return (physIfEntry);
	}

	public get IsLinked(): boolean {
		return this._ifTable.Some((pair) => {
			return pair.Value.IsLinked;
		});
	}

	public ClearLinks(): void {
		this._ifTable.ForEach((pair) => {
			pair.Value.RemoveLink();
		});
	}

	public ForEach(callbackfn: (value: IfEntry, index: number, array: IfEntry[]) => void, thisArg?: any): void {
		this._ifTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: IfEntry, index: number, array: IfEntry[]) => boolean, thisArg?: any): boolean {
		return this._ifTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: IfEntry, index: number, array: IfEntry[]) => boolean, thisArg?: any): boolean {
		return this._ifTable.Values().Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.if_info = {};
		obj = obj.if_info;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._ifTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.if_info)
			throw new Error();
		obj = obj.if_info;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new IfEntry();
				entry.Read(value, context);
				this.AddEntry(entry);
			}, this);
		}
	}
}
